/**
 * Database row ↔ TypeScript object conversion utilities.
 *
 * Supabase returns snake_case columns; our TypeScript interfaces use camelCase.
 * These helpers bridge the gap so every hook can convert consistently.
 */

/** Convert a snake_case DB row to a camelCase TypeScript object */
export function mapRow<T>(row: Record<string, unknown>): T {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(row)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())
    result[camelKey] = value
  }
  return result as T
}

/** Convert an array of snake_case DB rows */
export function mapRows<T>(rows: Record<string, unknown>[] | null): T[] {
  if (!rows) return []
  return rows.map((row) => mapRow<T>(row))
}

/**
 * Decrypt a field that was encrypted with encrypt_sensitive() in the database.
 * Calls the decrypt_field RPC. Returns plaintext when no encryption key is configured.
 * Returns null if the input is null/undefined.
 */
export async function decryptField(
  supabaseClient: {
    rpc: (fn: string, params: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>
  },
  encryptedValue: string | null,
): Promise<string | null> {
  if (!encryptedValue) return null
  const { data, error } = await supabaseClient.rpc('decrypt_field', {
    encrypted_value: encryptedValue,
  })
  if (error) return encryptedValue // Fallback to raw value if RPC fails
  return data as string
}

/** Convert a camelCase TypeScript object to a snake_case DB row for inserts/updates */
export function toDbRow(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) continue
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
    result[snakeKey] = value
  }
  return result
}
